import { useQuery } from '@tanstack/react-query';
import type {
  CampaignPerformanceResponseDto,
  TenantSalesMetricsResponseDto,
  TenantUserMetricsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useWithPrevious } from '@/hooks/use-with-previous';
import {
  appDownloadsChartQuery,
  campaignOrdersChartQuery,
  campaignPerformanceQuery,
  dataSufficiencyQuery,
  ordersCompletionQuery,
  revenueChartQuery,
  salesMetricsQuery,
  tabRange,
  userMetricsQuery,
  type AnalyticsTabParams,
} from '@/hooks/analytics/analytics-queries';

/**
 * Queries of the Resumo tab (ANLT-02): the three headline totals with the
 * previous period (CMP-01), their minigráficos, orders completion and the
 * data sufficiency. Same keys as the other tabs, so opening Vendas, Push or
 * Usuários afterwards reuses the cache.
 */
export function useOverviewTabData(params: AnalyticsTabParams) {
  const tenant = useTenant();
  const range = { ...tabRange(params), preset: params.preset };
  const { granularity, enabled } = params;

  const sales = useWithPrevious<TenantSalesMetricsResponseDto>(
    (r) => ({ ...salesMetricsQuery(tenant.slug, r), enabled }),
    range
  );
  const campaigns = useWithPrevious<CampaignPerformanceResponseDto>(
    (r) => ({ ...campaignPerformanceQuery(tenant.slug, r), enabled }),
    range
  );
  const users = useWithPrevious<TenantUserMetricsResponseDto>(
    (r) => ({ ...userMetricsQuery(tenant.slug, r), enabled }),
    range
  );
  const salesChart = useQuery({
    ...revenueChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const campaignOrdersChart = useQuery({
    ...campaignOrdersChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const appDownloadsChart = useQuery({
    ...appDownloadsChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const ordersCompletion = useQuery({
    ...ordersCompletionQuery(tenant.slug, range),
    enabled,
  });
  const dataSufficiency = useQuery({
    ...dataSufficiencyQuery(tenant.slug, range),
    enabled,
  });

  return {
    sales,
    campaigns,
    users,
    salesChart,
    campaignOrdersChart,
    appDownloadsChart,
    ordersCompletion,
    dataSufficiency,
  };
}
